/**
 * Invoice data model — a single normalized shape used to render an elegant
 * invoice document (on-screen preview + emailed HTML) for every billing kind:
 * tenant rent/deposit and owner management fees (and SaaS).
 *
 * Builders convert a DB row (+ related entities) into InvoiceData so the
 * document component stays dumb and reusable.
 */
import type { InvoiceSettlement, ManagementInvoice } from "@/types/billing";
import { isOffsetInvoice } from "@/types/billing";
import {
  splitGrossVat,
  managementInvoiceLines,
  managementRemainderOf,
  managementRemainderReference,
} from "./management-invoice-lines";

// Re-exported so existing importers keep their path; the implementation now
// lives in the shared (client+edge) management-invoice-lines module.
export { splitGrossVat } from "./management-invoice-lines";

export type InvoiceKind = "rent" | "deposit" | "management" | "saas";

export type InvoiceParty = {
  name: string;
  lines: string[]; // address / contact lines
  email?: string | null;
};

export type InvoiceLineItem = {
  description: string;
  detail?: string;
  /** Extra description lines rendered under the main description (e.g. Vergütung…, Berechnungsgrundlage…). */
  details?: string[];
  amountCents: number;
};

/** CoLiviq bank block printed in the invoice footer (Bankverbindung). */
export type InvoiceBank = {
  recipient: string;
  iban: string;
  bic?: string | null;
  bankName?: string | null;
};

export type InvoiceData = {
  kind: InvoiceKind;
  invoiceNo: string;
  issueDate: string; // ISO date
  dueDate: string | null;
  periodLabel: string; // e.g. "August 2026"
  currency: string;
  seller: InvoiceParty;
  billTo: InvoiceParty;
  /** Contextual facts shown in a compact meta grid (property, room, tenant…). */
  meta: { label: string; value: string }[];
  lineItems: InvoiceLineItem[];
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  notes?: string | null;

  // ── German management-invoice extras (optional; used by the SEV layout) ──
  /** Rendering locale — "de" switches InvoiceDocument to the German layout. */
  locale?: string;
  /** Salutation line, e.g. "Sehr geehrte Damen und Herren,". */
  salutation?: string;
  /** Intro paragraph under the "Rechnung" heading. */
  intro?: string;
  /** VAT rate as a whole number (e.g. 19). Drives the "zzgl. MwSt. 19 %" label. */
  vatRate?: number;
  /** Bank block for the footer. */
  bank?: InvoiceBank;
  /** Verwendungszweck reference. */
  reference?: string | null;
  /**
   * How this invoice settles — carried through so a renderer/consumer of the
   * data (not just the builder) can tell an offset invoice apart from a
   * missing bank account without re-deriving it. Management invoices only;
   * absent/null reads as "transfer" via isOffsetInvoice, matching the DB
   * default.
   */
  settlement?: InvoiceSettlement | null;
};

// Read each variable by name. Reading the env as a whole object makes Vite
// inline EVERY VITE_ variable of the build into the bundle, secrets included
// (COL-489/COL-523).

/** CoLiviq bank details — configurable via env so no placeholder IBAN ships. */
const COLIVIQ_IBAN = import.meta.env.VITE_COLIVIQ_IBAN?.trim() || "";
const COLIVIQ_BIC = import.meta.env.VITE_COLIVIQ_BIC?.trim() || "";
const COLIVIQ_VAT_ID = import.meta.env.VITE_COLIVIQ_VAT_ID?.trim() || "";

/**
 * §19 UStG note. CoLiviq does not charge VAT (small-business rule / reverse
 * charge), so invoices must SAY so rather than silently showing €0.00 VAT.
 * Override the exact wording via VITE_COLIVIQ_VAT_NOTE if the tax status changes.
 */
export const VAT_NOTE =
  import.meta.env.VITE_COLIVIQ_VAT_NOTE?.trim() ||
  "Gemäß §19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).";

/** CoLiviq issuer block — the seller on every invoice. Bank details for owners. */
export const COLIVIQ_SELLER: InvoiceParty = {
  name: "Coliviq GmbH",
  lines: [
    "coliviq Property Management",
    "rechnungen@coliviq.de",
    ...(COLIVIQ_VAT_ID ? [`USt-IdNr.: ${COLIVIQ_VAT_ID}`] : []),
    ...(COLIVIQ_IBAN ? [`IBAN: ${COLIVIQ_IBAN}`] : []),
    ...(COLIVIQ_BIC ? [`BIC: ${COLIVIQ_BIC}`] : []),
  ],
  email: "rechnungen@coliviq.de",
};

/**
 * Registered address + phone for the German invoice header. Overridable via env
 * so a move or number change doesn't need a code change; fall back to the values
 * printed on the CoLiviq invoice template.
 */
const COLIVIQ_ADDRESS =
  import.meta.env.VITE_COLIVIQ_ADDRESS?.trim() ||
  "Elvirastraße 10 · 80636 München";
const COLIVIQ_PHONE =
  import.meta.env.VITE_COLIVIQ_PHONE?.trim() || "+49 89 54193354";

/**
 * German seller block for the SEV invoice header (address + contact). The
 * USt-IdNr. is printed only when configured via env (VITE_COLIVIQ_VAT_ID);
 * management fees carry 19 % MwSt (see MGMT_VAT_RATE).
 */
export const COLIVIQ_SELLER_DE: InvoiceParty = {
  name: "Coliviq GmbH",
  lines: [COLIVIQ_ADDRESS, `${COLIVIQ_PHONE} · rechnungen@coliviq.de`],
  email: "rechnungen@coliviq.de",
};

// ── German date / money helpers for the SEV invoice ──────────────────
function deLongDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function deShortDate(d: Date): string {
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Days after the invoice date that a management fee is due. */
export const MGMT_DUE_DAYS = 10;

/**
 * What an OFFSET invoice prints where a payable one prints its Fälligkeitsdatum
 * and its Bankverbindung.
 *
 * German, like the rest of this document (it is the legal invoice the owner
 * files), and deliberately explicit that nothing is to be transferred — an
 * owner reading a €1.250 invoice must not be left wondering whether they owe
 * it. Mirrored verbatim in supabase/functions/send-invoice/index.ts, which
 * renders the emailed copy of the same document.
 */
export const MGMT_OFFSET_PAYMENT_LABEL = "Verrechnung mit vereinnahmter Miete";

/**
 * The settlement note, in the tense that is actually TRUE of this invoice.
 *
 * An offset invoice is sent to the owner as their bookkeeping document as soon
 * as it is issued — which is normally BEFORE an admin has confirmed the
 * deduction against the month's collected rent. A single past-tense note
 * ("wurde verrechnet") would hand the owner a settlement record for something
 * that had not happened yet, which is the one claim a legal invoice must not
 * make. So the note follows the invoice's own state: announced while pending,
 * asserted once settled.
 *
 * "Eine Zahlung ist nicht erforderlich" holds either way — the owner never
 * transfers this fee, whichever side of the confirmation the document is on.
 */
export function mgmtOffsetNoteDe(settled: boolean): string {
  return settled
    ? "Dieser Betrag wurde mit der von uns für den Leistungszeitraum vereinnahmten Miete verrechnet. Eine Zahlung ist nicht erforderlich."
    : "Dieser Betrag wird mit der von uns für den Leistungszeitraum vereinnahmten Miete verrechnet. Eine Zahlung ist nicht erforderlich.";
}

/** Today as YYYY-MM-DD (local). */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Add whole days to a YYYY-MM-DD date, returning YYYY-MM-DD. */
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Rechnungsdatum shown on the document. Once an invoice is issued (sent),
 * issue_date is frozen and used forever. Before that it has no date, so we
 * render today as a live draft — the invoice isn't officially dated until sent.
 */
export function effectiveInvoiceDate(
  inv: Pick<ManagementInvoice, "issue_date">
): string {
  return inv.issue_date ? String(inv.issue_date).slice(0, 10) : todayIso();
}

/**
 * Fälligkeitsdatum shown on the document. Issued invoices keep their frozen
 * due_date; drafts derive it from today so it stays exactly {@link MGMT_DUE_DAYS}
 * days after the (draft) invoice date rather than a stale generation-time value.
 *
 * An OFFSET invoice has no due date at all, draft or issued: the fee is
 * deducted from the rent CoLiviq collected, so there is no date by which the
 * owner must transfer anything. Deriving one for the draft would print a
 * payment deadline on a document that asks for no payment.
 */
export function effectiveDueDate(
  inv: Pick<ManagementInvoice, "issue_date" | "due_date"> & {
    settlement?: InvoiceSettlement | null;
  }
): string | null {
  if (isOffsetInvoice(inv)) return null;
  if (inv.issue_date)
    return inv.due_date ? String(inv.due_date).slice(0, 10) : null;
  return addDaysIso(todayIso(), MGMT_DUE_DAYS);
}

function deEuros(cents: number): string {
  return `${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents ?? 0) / 100)} EUR`;
}

function monthLabel(periodMonth: string): string {
  // periodMonth is "YYYY-MM-01"
  const d = new Date(`${periodMonth.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return periodMonth;
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

export type ManagementInvoiceContext = {
  ownerName?: string | null;
  ownerLines?: string[];
  ownerEmail?: string | null;
  propertyTitle?: string | null;
  /** Full property location (street + city) for the paragraph + line item. */
  propertyAddress?: string | null;
  apartmentLabel?: string | null;
  roomsCount?: number | null;
  /**
   * Resolved CoLiviq "pay to" bank block for the Bankverbindung — the platform
   * (CoLiviq-held) account for this invoice's apartment/property (see
   * resolvePlatformAccount). When omitted/null, no bank block is rendered and
   * Send is gated off (matching the edge function, which 400s).
   */
  bank?: InvoiceBank | null;
};

/**
 * VAT rate on CoLiviq management fees. Management fees are billed with 19 %
 * German VAT (MwSt.): the stored amount is gross (incl. VAT) and split into
 * net / VAT / gross below. Setting this to 0 falls back to the §19 UStG
 * Kleinunternehmer note (the split logic is gated on `> 0`), so a change of
 * tax status is a one-line flip.
 */
export const MGMT_VAT_RATE = 19;

/** Derive the "Berechnungsgrundlage" text from the invoice's fee_basis. */
function managementBasisText(inv: ManagementInvoice): string {
  // COL-483: a remainder bills the month's fee above what an earlier invoice
  // already locked, so the month's own rate would misdescribe its amount.
  const previous = managementRemainderOf(inv);
  if (previous) return managementRemainderReference(previous);
  const fb = (inv.fee_basis || {}) as Record<string, unknown>;
  const model = typeof fb.revenue_model === "string" ? fb.revenue_model : "";
  const rooms = Number(fb.occupied_rooms);
  const feeAmt = Number(fb.coliviq_fee_amount);
  const feePct = Number(fb.coliviq_fee_pct);
  if (model === "flat_fee_per_room" && rooms > 0 && feeAmt > 0) {
    return `${rooms} Zimmer × ${deEuros(Math.round(feeAmt * 100))}`;
  }
  if (model === "pct_of_rent" && feePct > 0) {
    return `${new Intl.NumberFormat("de-DE").format(feePct)} % der Nettokaltmiete`;
  }
  if (typeof fb.summary === "string" && fb.summary) return fb.summary;
  return inv.description || "Co-Living-Management";
}

/**
 * Flat map of INVOICE_* field name → stamped value for the uploaded invoice
 * template (drag-and-drop stamping). Amounts carry the "EUR" suffix (e.g.
 * "336,13 EUR"): the whiteout box painted under each field wipes the template's
 * own pre-printed "EUR", so the stamped value must include it. Dates are long
 * German dates.
 *
 * The offset branch below is DEFENCE, not a live path: an offset invoice never
 * reaches the stamped template at all — both callers skip it outright
 * (useManagementInvoiceActions.tsx's `!offset`, and send-invoice's
 * `!isOffsetManagementInvoice`), because the overlay can only replace named
 * fields and has no way to blank out a template's own pre-printed IBAN. It is
 * kept so that re-enabling that path cannot silently stamp an unexplained dash
 * into the Fälligkeitsdatum box: `deLongDate(null)` returns "—", which the
 * stamper treats as a real value since it only skips null/empty.
 */
export function managementInvoiceFieldValues(
  inv: ManagementInvoice,
  ctx: ManagementInvoiceContext = {}
): Record<string, string> {
  const { netCents, vatCents, grossCents } = splitGrossVat(
    inv.amount_cents,
    MGMT_VAT_RATE
  );
  const startIso = inv.period_month.slice(0, 10);
  const start = new Date(`${startIso}T12:00:00`);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 12);
  const [street = "", postalCity = ""] = ctx.ownerLines ?? [];
  return {
    OWNER_NAME: ctx.ownerName || "",
    OWNER_STREET_ADDRESS: street,
    OWNER_POSTAL_CITY: postalCity,
    PROPERTY_ADDRESS: ctx.propertyAddress || ctx.propertyTitle || "",
    INVOICE_NUMBER: inv.invoice_no || "",
    INVOICE_DATE: deLongDate(effectiveInvoiceDate(inv)),
    SERVICE_PERIOD: `${deShortDate(start)} – ${deShortDate(end)}`,
    DUE_DATE: isOffsetInvoice(inv)
      ? MGMT_OFFSET_PAYMENT_LABEL
      : deLongDate(effectiveDueDate(inv)),
    MANAGEMENT_FEE: managementBasisText(inv),
    NET_AMOUNT: deEuros(netCents),
    VAT_AMOUNT: deEuros(vatCents),
    TOTAL_AMOUNT: deEuros(grossCents),
  };
}

/** Build InvoiceData for an owner management-fee invoice (German SEV layout). */
export function buildManagementInvoiceData(
  inv: ManagementInvoice,
  ctx: ManagementInvoiceContext = {}
): InvoiceData {
  const period = monthLabel(inv.period_month);
  const ownerName = ctx.ownerName || "dem Eigentümer";
  const property = ctx.propertyAddress || ctx.propertyTitle || "";

  // Service period = the whole covered month.
  const startIso = inv.period_month.slice(0, 10);
  const start = new Date(`${startIso}T12:00:00`);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 12);
  const endIso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
  const periodRange = `${deShortDate(start)} – ${deShortDate(end)}`;

  const basisText = managementBasisText(inv);

  // Line items + reconciled totals from the SHARED helper (identical to the
  // emailed invoice). Amount is stored gross; net/VAT/gross are split from it.
  // itemized === one line per property (consolidated 2+ property invoice).
  const amounts = managementInvoiceLines(
    inv.fee_basis as Record<string, unknown> | null,
    inv.amount_cents,
    MGMT_VAT_RATE,
    `Verwaltungsgebühr – Co-Living-SEV-Management${property ? ` ${property}` : ""}`
  );
  const { netCents, vatCents, grossCents } = amounts;

  const svcDetail =
    `Vergütung für vollumfängliches Co-Living-Management gemäß Sondereigentumsverwaltungsvertrag (SEV) ` +
    `im Leistungszeitraum ${deLongDate(startIso)} bis ${deLongDate(endIso)}.`;
  const lineItems: InvoiceLineItem[] = amounts.itemized
    ? amounts.lines.map((l) => ({
        description: `Verwaltungsgebühr – ${l.title}`,
        details: [svcDetail],
        amountCents: l.netCents,
      }))
    : [
        {
          description: `Verwaltungsgebühr – Co-Living-SEV-Management${property ? ` ${property}` : ""}`,
          details: [
            svcDetail,
            `Berechnungsgrundlage: ${basisText}${MGMT_VAT_RATE > 0 ? ` inkl. ${MGMT_VAT_RATE} % MwSt.` : ""}`,
          ],
          amountCents: netCents,
        },
      ];

  // An offset invoice replaces the payment deadline with how it is settled.
  // Printing "Fälligkeitsdatum: —" would read as a missing field on a legal
  // document; naming the Verrechnung says the fee is already accounted for.
  const offset = isOffsetInvoice(inv);
  const meta = [
    { label: "Rechnungsnummer", value: inv.invoice_no || "—" },
    { label: "Rechnungsdatum", value: deLongDate(effectiveInvoiceDate(inv)) },
    { label: "Leistungszeitraum", value: periodRange },
    offset
      ? { label: "Zahlungsweise", value: MGMT_OFFSET_PAYMENT_LABEL }
      : { label: "Fälligkeitsdatum", value: deLongDate(effectiveDueDate(inv)) },
  ];

  // COL-483: a remainder names the invoice it follows, right under its own
  // number, so the owner can read the two together.
  const previous = managementRemainderOf(inv);
  if (previous) {
    meta.splice(1, 0, {
      label: "Bezug",
      value: managementRemainderReference(previous),
    });
  }

  return {
    kind: "management",
    locale: "de",
    invoiceNo: inv.invoice_no || "—",
    issueDate: effectiveInvoiceDate(inv),
    dueDate: effectiveDueDate(inv),
    periodLabel: period,
    currency: inv.currency,
    seller: COLIVIQ_SELLER_DE,
    billTo: {
      name: ownerName,
      lines: ctx.ownerLines || [],
      email: ctx.ownerEmail,
    },
    meta,
    salutation: "Sehr geehrte Damen und Herren,",
    intro:
      `gemäß der bestehenden Sondereigentumsverwaltungsvereinbarung (SEV) zwischen Coliviq GmbH und ${ownerName} ` +
      (previous
        ? // COL-483: a remainder bills the fee on the rent received after the
          // invoice it follows.
          `berechnen wir hiermit die Verwaltungsgebühr für ${amounts.itemized ? "Ihre Co-Living-Objekte" : `das Co-Living-Objekt${property ? ` ${property}` : ""}`} ` +
          `auf die Miete nach, die nach Rechnung ${previous} für den ausgewiesenen Leistungszeitraum eingegangen ist. `
        : `stellen wir hiermit unsere Verwaltungsgebühr für ${amounts.itemized ? "Ihre Co-Living-Objekte" : `das Co-Living-Objekt${property ? ` ${property}` : ""}`} in Rechnung. `) +
      `Die nachfolgenden Leistungen umfassen den vollumfänglichen Betrieb, die Vermietung und das Mietermanagement ` +
      `${amounts.itemized ? "der Objekte" : "des Objekts"} im ausgewiesenen Leistungszeitraum.`,
    lineItems,
    subtotalCents: netCents,
    taxCents: vatCents,
    totalCents: grossCents,
    vatRate: MGMT_VAT_RATE,
    // The resolved platform (CoLiviq) account for this apartment, or no bank
    // block if none resolves. DB-only, matching the edge function exactly — the
    // preview must never enable Send on an account the send path would reject.
    //
    // An offset invoice prints NO Bankverbindung: the fee was deducted from
    // rent CoLiviq holds, so an IBAN and a "Fällig am" would instruct the owner
    // to transfer money they do not owe — the single worst thing this document
    // could get wrong.
    bank: offset ? undefined : (ctx.bank ?? undefined),
    reference: inv.invoice_no,
    settlement: inv.settlement,
    notes:
      // §19 UStG note first (legal), then the closing pleasantry. When VAT is
      // charged (MGMT_VAT_RATE > 0) the Kleinunternehmer note no longer applies.
      (MGMT_VAT_RATE > 0 ? "" : `${VAT_NOTE} `) +
      (offset ? `${mgmtOffsetNoteDe(inv.status === "paid")} ` : "") +
      "Bei Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung. " +
      "Wir danken für die vertrauensvolle Zusammenarbeit.",
  };
}
